import classNames from "classnames";
import { KeyboardEvent, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import KuikaBadge from "../../../ui-kit/kuika-badge/kuika-badge";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSmallSpinner from "../../../ui-kit/kuika-small-spinner/kuika-small-spinner";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabContent from "../../../ui-kit/kuika-tab-content/kuika-tab-content";
import KuikaTabs from "../../../ui-kit/kuika-tabs/kuika-tabs";
import "./kuika-data-type-dropdown.scss";

/**
 * The panel of an in-cell type picker: a search box over the types, split into categories with
 * one tab each. It renders ONLY the panel — no trigger, no popover — so any combo can host it in
 * its own popover. `KuikaDataTypeDropdown` is its two-tab preset (primitives + the project's data
 * types); the table editor's column Type cell hands it one tab per schema.
 *
 * Search runs over every category at once, so a type is found without knowing where it lives:
 * each tab counts its matches, and a miss on the open tab moves to the first tab that has one.
 */

/** One row of a category's list. */
export interface IDataTypePanelOption {
  value: string;
  /** What the row reads and what the search matches — the full name, a schema prefix included. */
  label: string;
  /** Any node, so a host can hand a `KuikaSvg` or an SVG component of its own. */
  icon?: ReactNode;
  testId?: string;
}

/** One tab. */
export interface IDataTypePanelCategory {
  key: string;
  label: string;
  options: IDataTypePanelOption[];
  /** The list is (re)loading: the tab shows a spinner instead of a stale list. */
  loading?: boolean;
  testId?: string;
}

export interface IKuikaDataTypePanelProps {
  categories: IDataTypePanelCategory[];
  /** The current value; unless `defaultCategory` says otherwise, the panel opens on the tab that holds it. */
  value?: string;
  /** The tab the panel always opens on, wherever the value lives. */
  defaultCategory?: string;
  onSelect: (value: string, categoryKey: string) => void;
  /** Called after a pick so the host popover can close. */
  onRequestClose?: () => void;
  /** Drawn under the list, e.g. a "Manage" link. */
  footer?: ReactNode;
  className?: string;
  testId?: string;
  searchTestId?: string;
  tabsTestId?: string;
}

/** The categories' matches for one query, keyed by category. */
type CategoryMatches = Map<string, IDataTypePanelOption[]>;

/** A category as the tab strip takes it. */
interface ITabStripOption {
  value: string;
  label: ReactNode;
  dataTestId?: string;
}

/** Case-insensitive substring on the whole label. `toLowerCase` is locale-independent, unlike its locale twin. */
const findMatches = (categories: IDataTypePanelCategory[], query: string): CategoryMatches => {
  const needle: string = query.trim().toLowerCase();
  const matches: CategoryMatches = new Map<string, IDataTypePanelOption[]>();
  categories.forEach((category: IDataTypePanelCategory): void => {
    // A loading tab has nothing to offer yet, so it never counts as a match.
    const options: IDataTypePanelOption[] = category.loading ? [] : category.options;
    matches.set(
      category.key,
      needle
        ? options.filter((option: IDataTypePanelOption): boolean => option.label.toLowerCase().includes(needle))
        : options
    );
  });
  return matches;
};

/** The tab a value lives on, or the first tab when it lives on none. */
const categoryOf = (categories: IDataTypePanelCategory[], value: string | undefined): string => {
  const holder: IDataTypePanelCategory | undefined =
    value === undefined
      ? undefined
      : categories.find((category: IDataTypePanelCategory): boolean =>
          category.options.some((option: IDataTypePanelOption): boolean => option.value === value)
        );
  return holder?.key ?? categories[0]?.key ?? "";
};

/**
 * The tab a search should jump to: the first (in tab order) with a match, unless the open tab
 * already has one — a query that leaves the open tab empty is what searching across tabs is for,
 * but a query the open tab can already answer stays where it is. `undefined` means no jump: no
 * query, the open tab already has a match, or nothing anywhere does.
 */
const firstMatchingTab = (
  categories: IDataTypePanelCategory[],
  keys: string[],
  query: string,
  activeTab: string
): string | undefined => {
  if (!query.trim()) return undefined;
  const matches: CategoryMatches = findMatches(categories, query);
  if ((matches.get(activeTab)?.length ?? 0) > 0) return undefined;
  return keys.find((key: string): boolean => (matches.get(key)?.length ?? 0) > 0);
};

export const KuikaDataTypePanel = ({
  categories,
  value,
  defaultCategory,
  onSelect,
  onRequestClose,
  footer,
  className,
  testId,
  searchTestId,
  tabsTestId
}: IKuikaDataTypePanelProps): JSX.Element => {
  const [query, setQuery] = useState<string>("");
  const [requestedTab, setRequestedTab] = useState<string>(
    (): string => defaultCategory ?? categoryOf(categories, value)
  );
  const rootRef = useRef<HTMLDivElement>(null);
  const tabStripRef = useRef<HTMLDivElement>(null);

  const keys: string[] = useMemo(
    (): string[] => categories.map((category: IDataTypePanelCategory): string => category.key),
    [categories]
  );
  const matches: CategoryMatches = useMemo((): CategoryMatches => findMatches(categories, query), [categories, query]);
  // The categories can change under an open panel (a list that finishes loading); a tab that went
  // away falls back to the first rather than leaving the panel on nothing.
  const activeTab: string = keys.includes(requestedTab) ? requestedTab : (keys[0] ?? "");

  /**
   * The tab strip scrolls sideways when the categories outgrow the panel (a workspace with many
   * schemas). A mouse wheel only scrolls vertically, which this strip cannot, so it is turned into
   * the sideways scroll the strip does have — the way a browser's own tab strip reads it.
   */
  useEffect((): (() => void) | void => {
    const strip: HTMLDivElement | null = tabStripRef.current;
    if (!strip) return;
    const onWheel = (event: WheelEvent): void => {
      if (strip.scrollWidth <= strip.clientWidth) return;
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      strip.scrollLeft += event.deltaY;
      event.preventDefault();
    };
    // Not passive: the page behind the popover would scroll as well otherwise.
    strip.addEventListener("wheel", onWheel, { passive: false });
    return (): void => strip.removeEventListener("wheel", onWheel);
  }, []);

  // A tab chosen off-screen — by the search's jump, or on open — is scrolled into the strip's view.
  useEffect((): void => {
    const strip: HTMLDivElement | null = tabStripRef.current;
    const item: HTMLElement | undefined =
      strip?.querySelectorAll<HTMLElement>(".rc-segmented-item")[keys.indexOf(activeTab)];
    if (!strip || !item) return;
    const stripBox: DOMRect = strip.getBoundingClientRect();
    const itemBox: DOMRect = item.getBoundingClientRect();
    if (itemBox.left < stripBox.left) {
      strip.scrollLeft -= stripBox.left - itemBox.left;
    } else if (itemBox.right > stripBox.right) {
      strip.scrollLeft += itemBox.right - stripBox.right;
    }
  }, [activeTab, keys]);

  // A category can still be `loading` — or simply not fetched yet — the moment the panel opens, so
  // a query typed right away searches against categories with nothing in them: `changeQuery`'s own
  // jump below finds no match anywhere and never runs again, since it only fires on the keystroke.
  // Retrying here, whenever `categories` itself changes, catches the list arriving a beat late —
  // otherwise a fast search lands the picker on Primitive with "No matching type" forever, badges
  // and all, even once the real list is in. `query`/`activeTab` are this render's already-current
  // values; only a change to the categories the fetch controls should re-run this.
  useEffect((): void => {
    const withMatches: string | undefined = firstMatchingTab(categories, keys, query, activeTab);
    if (withMatches) setRequestedTab(withMatches);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories]);

  const changeQuery = (next: string): void => {
    setQuery(next);
    const withMatches: string | undefined = firstMatchingTab(categories, keys, next, activeTab);
    if (withMatches) setRequestedTab(withMatches);
  };

  const select = (optionValue: string, categoryKey: string): void => {
    onSelect(optionValue, categoryKey);
    setQuery("");
    onRequestClose?.();
  };

  /** The rows of the open tab, in the order drawn — during a swap the leaving pane is still in the DOM. */
  const activeOptionButtons = (): HTMLElement[] => {
    const panes: HTMLElement[] = Array.from(
      rootRef.current?.querySelectorAll<HTMLElement>("[data-type-panel-pane]") ?? []
    );
    const pane: HTMLElement | undefined = panes.find(
      (candidate: HTMLElement): boolean => candidate.dataset.typePanelPane === activeTab
    );
    return pane ? Array.from(pane.querySelectorAll<HTMLElement>(".kuika-data-type-dropdown__option")) : [];
  };

  const searchInput = (): HTMLInputElement | null =>
    rootRef.current?.querySelector<HTMLInputElement>(".kuika-data-type-dropdown__search input") ?? null;

  /** ↓ from the search box enters the list, ↑ from its first row goes back up; neither wraps. */
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    const options: HTMLElement[] = activeOptionButtons();
    const search: HTMLInputElement | null = searchInput();
    const target: EventTarget = event.target;
    const index: number = options.findIndex((option: HTMLElement): boolean => option === target);
    if (target === search) {
      if (event.key === "ArrowDown" && options.length > 0) {
        event.preventDefault();
        options[0].focus();
      }
      return;
    }
    if (index === -1) return;
    event.preventDefault();
    if (event.key === "ArrowDown") {
      options[Math.min(index + 1, options.length - 1)].focus();
    } else if (index === 0) {
      search?.focus();
    } else {
      options[index - 1].focus();
    }
  };

  /** Enter in the search box takes the open tab's first match — type, Enter, done. */
  const pickFirstMatch = (event: KeyboardEvent<HTMLInputElement>): void => {
    // Enter that confirms an IME composition is not a pick.
    if (event.nativeEvent.isComposing) return;
    const first: IDataTypePanelOption | undefined = matches.get(activeTab)?.[0];
    if (!first) return;
    event.preventDefault();
    select(first.value, activeTab);
  };

  // While searching, each tab carries a count badge (blue on the open tab, grey on the others) so a
  // match on a tab that is not open is visible without going to it.
  const tabLabel = (category: IDataTypePanelCategory): ReactNode => {
    if (!query) return category.label;
    return (
      <span className="kuika-data-type-dropdown__tab-label">
        {category.label}
        <KuikaBadge
          type="label"
          size="small"
          color={category.key === activeTab ? "blue" : "grey"}
          content={String(matches.get(category.key)?.length ?? 0)}
        />
      </span>
    );
  };

  const renderOption = (option: IDataTypePanelOption, categoryKey: string): JSX.Element => (
    <button
      key={option.value}
      type="button"
      className={classNames("kuika-data-type-dropdown__option", {
        "kuika-data-type-dropdown__option--selected": option.value === value
      })}
      data-testid={option.testId}
      onClick={(): void => select(option.value, categoryKey)}
    >
      <span className="kuika-data-type-dropdown__option-check">
        {option.value === value && <KuikaSvg name="check" width={12} height={12} color="var(--blue-5)" />}
      </span>
      {option.icon}
      <span className="kuika-data-type-dropdown__option-label" title={option.label}>
        {option.label}
      </span>
    </button>
  );

  /**
   * One tab's options. Drawn from its own `key`, not the open tab, so the pane leaving during a
   * tab swap is still the list it was.
   */
  const renderOptionsBody = (key: string): ReactNode => {
    const category: IDataTypePanelCategory | undefined = categories.find(
      (candidate: IDataTypePanelCategory): boolean => candidate.key === key
    );
    if (category?.loading) {
      return (
        <div className="kuika-data-type-dropdown__loading">
          <KuikaSmallSpinner spinning />
        </div>
      );
    }
    const options: IDataTypePanelOption[] = matches.get(key) ?? [];
    if (options.length === 0) {
      return <span className="kuika-data-type-dropdown__no-match">No matching type</span>;
    }
    return options.map((option: IDataTypePanelOption): JSX.Element => renderOption(option, key));
  };

  return (
    <div
      ref={rootRef}
      className={classNames("kuika-data-type-dropdown", className)}
      data-testid={testId}
      onKeyDown={handleKeyDown}
    >
      <KuikaInput
        className="kuika-data-type-dropdown__search"
        size="md"
        borderless
        autoFocus
        placeholder="Search..."
        value={query}
        leftIcon={<KuikaSvg name="search" width={13} height={13} color="var(--dark-5)" />}
        onChange={(_e: React.ChangeEvent<HTMLInputElement>, next: string | number): void => changeQuery(String(next))}
        onPressEnter={pickFirstMatch}
        data-testid={searchTestId}
      />

      <div className="kuika-data-type-dropdown__tab-strip" ref={tabStripRef}>
        <KuikaTabs
          className="kuika-data-type-dropdown__tabs"
          size="lg"
          dataTestId={tabsTestId}
          options={categories.map((category: IDataTypePanelCategory): ITabStripOption => ({
            value: category.key,
            label: tabLabel(category),
            dataTestId: category.testId
          }))}
          value={activeTab}
          onChange={setRequestedTab}
        />
      </div>

      <div className="kuika-data-type-dropdown__options">
        <KuikaTabContent
          activeKey={activeTab}
          order={keys}
          render={(key: string): ReactNode => (
            <div className="kuika-data-type-dropdown__pane" data-type-panel-pane={key}>
              {renderOptionsBody(key)}
            </div>
          )}
        />
      </div>

      {footer}
    </div>
  );
};

export default KuikaDataTypePanel;
